import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { useProfilePicture } from '../hooks/useProfilePicture';
import { COLORS, FONTS, SHADOWS, GRADIENTS } from '../styles/tokens';

interface ProfileModalProps {
  onClose: () => void;
}

/** Full-screen overlay with a neumorphic profile settings card */
export function ProfileModal({ onClose }: ProfileModalProps): React.JSX.Element {
  const { isDark } = useTheme();
  const { username, fullName, email, roles } = useAuth();
  const { pictureUrl, uploadPicture, removePicture } = useProfilePicture(username);
  const colors = isDark ? COLORS.dark : COLORS.light;
  const isAdmin = roles.includes('admin');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  /** Close on Escape */
  useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    try {
      await uploadPicture(file);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  /** Removal now round-trips to the server, so it can fail like an upload. */
  async function handleRemove(): Promise<void> {
    setUploadError(null);
    try {
      await removePicture();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to remove picture');
    }
  }

  const org = 'Department of Defense';
  const roleDisplay = isAdmin ? 'Administrator' : 'User';

  const infoRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px solid ${colors.border}`,
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: FONTS.heading,
    fontSize: '10px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: colors.textSecondary,
  };
  const valueStyle: React.CSSProperties = {
    fontFamily: FONTS.body,
    fontSize: '13px',
    color: colors.text,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 200,
      }}
    >
      <div
        className="vx-modal-pad"
        onClick={e => e.stopPropagation()}
        style={{
          width: '420px',
          maxWidth: '90vw',
          backgroundColor: colors.card,
          borderRadius: '16px',
          boxShadow: isDark ? SHADOWS.neumorphicDark : SHADOWS.neumorphicLight,
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{
            fontFamily: FONTS.heading,
            fontSize: '14px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: colors.text,
            margin: 0,
          }}>
            PROFILE SETTINGS
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: colors.textSecondary,
              padding: '4px',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Avatar section */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: isDark ? COLORS.accent.blueDark : COLORS.accent.blue,
              display: 'grid',
              placeItems: 'center',
              color: '#FFFFFF',
              fontFamily: FONTS.heading,
              fontSize: '28px',
              fontWeight: 600,
              border: isAdmin ? `3px solid ${COLORS.accent.gold}` : 'none',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {pictureUrl ? (
              <img src={pictureUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              (fullName || username || 'U').charAt(0).toUpperCase()
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: isDark ? GRADIENTS.buttonDark : GRADIENTS.buttonLight,
                border: 'none',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontFamily: FONTS.heading,
                fontSize: '10px',
                letterSpacing: '0.15em',
                padding: '6px 14px',
                cursor: 'pointer',
              }}
            >
              UPLOAD
            </button>
            {pictureUrl && (
              <button
                onClick={() => { void handleRemove(); }}
                style={{
                  background: 'none',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  color: colors.textSecondary,
                  fontFamily: FONTS.heading,
                  fontSize: '10px',
                  letterSpacing: '0.15em',
                  padding: '6px 14px',
                  cursor: 'pointer',
                }}
              >
                REMOVE
              </button>
            )}
          </div>
          {uploadError && (
            <p style={{ fontFamily: FONTS.body, fontSize: '11px', color: '#EF4444', margin: 0 }}>{uploadError}</p>
          )}
        </div>

        {/* User info */}
        <div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>NAME</span>
            <span style={valueStyle}>{fullName || username}</span>
          </div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>USERNAME</span>
            <span style={valueStyle}>{username}</span>
          </div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>EMAIL</span>
            <span style={valueStyle}>{email || '—'}</span>
          </div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>ORGANIZATION</span>
            <span style={valueStyle}>{org}</span>
          </div>
          <div style={{ ...infoRowStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>ROLE</span>
            <span style={{
              ...valueStyle,
              color: isAdmin ? COLORS.accent.gold : colors.text,
              fontWeight: isAdmin ? 600 : 400,
            }}>
              {roleDisplay}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
